import { Link, useLocation } from "react-router-dom";
import queryString from "query-string";
import Footer from "@/components/shared/Footer";
import { Button } from "../shared/Button";
import ExternalLink from "../shared/ExternalLink";
import { SessionManager } from "@/services/SessionManager";

const LoginLink = ({ children }: { children: string }) => (
  <Link to="/login" className="text-hex-337ab7 cursor-pointer font-medium hover:underline">
    {children}
  </Link>
);

const ErrorMessage = ({ children }: { children: React.ReactNode }) => (
  <div className="font-medium text-base text-hex-ee5042 leading-[1.7]">
    {children}
  </div>
);

const UserGoogleAuthDisabledMessage = () => (
  <ErrorMessage>
    Your attempt to log in with Google was rejected. Your personal Replicated account
    has disabled Google Auth as an option. Try <LoginLink>logging in</LoginLink> with
    your username/password (or SAML if setup) and then visit your account settings to
    re-enable Google Auth.
  </ErrorMessage>
);

const TeamGoogleAuthDisabledMessage = () => (
  <ErrorMessage>
    Your attempt to log in with Google was rejected. Your Replicated team admin has
    disabled Google Auth for everyone. Try <LoginLink>logging in</LoginLink> with your
    username/password (or SAML if setup) or ask an administrator to adjust your team
    settings to allow Google Auth.
  </ErrorMessage>
);

const TrialExpiredButExtendableMessage = ({ nonce }) => (
  <ErrorMessage>
    <Button
      kind="primary-purple"
      size="large"
      className="flex-none mt-3"
      onClick={async () => {
        const response = await fetch(
          `${SessionManager.getApiEndpoint()}/team/extend-trial`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json"
            },
            body: JSON.stringify({ nonce })
          }
        );

        if (response.ok) {
          alert("Your trial has been extended by 12 hours. Click ok to log in again");
          window.location.href = "/login";
        }
      }}
    >
      Auto-extend trial by 12 hours
    </Button>
  </ErrorMessage>
);

const TrialExpiredMessage = () => (
  <ErrorMessage>
    Your free trial has ended. Please contact us at{" "}
    <ExternalLink
      href="mailto:contact@replicated.com"
      className="text-hex-337ab7 font-medium hover:underline hover:cursor-pointer"
    >
      contact@replicated.com
    </ExternalLink>{" "}
    if you need an extension.
  </ErrorMessage>
);

const MarketingPanel = () => (
  <div className="flex flex-1 flex-col items-center self-center mx-auto max-lg:mb-[70px] lg:flex lg:flex-col">
    <div className="relative bg-[top_center] bg-[url('../../images/global/replicated-logo-red.svg')] bg-no-repeat bg-[length:350px] w-[586px] h-[180px] mt-[70px] max-lg:static max-lg:bg-[length:250px] max-lg:w-full max-lg:h-[100px] max-lg:!h-[60px]"></div>
  </div>
);

const FormPanel = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-page-bg lg:ml-0 lg:text-left flex-1 min-h-0 flex flex-col items-center justify-center pb-3 auth-pages">
    <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0">{children}</div>
  </div>
);

const ErrorTitle = ({ children }: { children: string }) => (
  <>
    <span className="icon clickable google-g mr-3" />
    <p className="font-['Montserrat',sans-serif] font-bold text-hex-f44247 text-[30px] !text-left max-lg:mt-[70px] leading-[1.4]">{children}</p>
  </>
);

const Page = ({ children }: { children: React.ReactNode }) => (
      <div className="h-full w-full flex flex-col overflow-y-auto">
    <div className="block min-[800px]:flex min-[800px]:flex-row flex-auto w-full">
      {children}
      <div className="absolute bottom-[10px] right-[10px] !text-gray-200 max-lg:mx-auto max-lg:pt-[10px] max-lg:static max-lg:w-full max-lg:text-center max-lg:min-h-[100px]">
        <Footer />
      </div>
    </div>
  </div>
);

const AuthGoogleError = () => {
  const location = useLocation();
  const errorType = queryString.parse(location.search).error;

  const renderErrorMessage = () => {
    switch (errorType) {
      case "TEAM_GOOGLE_AUTH_DISABLED": {
        return <TeamGoogleAuthDisabledMessage />;
      }
      case "USER_GOOGLE_AUTH_DISABLED": {
        return <UserGoogleAuthDisabledMessage />;
      }
      case "TRIAL_EXPIRED_ALLOW_EXTENSION": {
        const nonce = queryString.parse(location.search).nonce;
        return <TrialExpiredButExtendableMessage nonce={nonce as string} />;
      }
      case "TRIAL_EXPIRED": {
        return <TrialExpiredMessage />;
      }
      default: {
        return <UserGoogleAuthDisabledMessage />;
      }
    }
  };

  const renderTitle = () => {
    switch (errorType) {
      case "TRIAL_EXPIRED_ALLOW_EXTENSION": {
        return <ErrorTitle>Trial Has Ended. But...</ErrorTitle>;
      }
      case "TRIAL_EXPIRED": {
        return <ErrorTitle>Your Trial Has Ended</ErrorTitle>;
      }
      default: {
        return <ErrorTitle>Authentication Error</ErrorTitle>;
      }
    }
  };

  const renderBelowMessage = () => {
    switch (errorType) {
      case "TRIAL_EXPIRED_ALLOW_EXTENSION":
      case "TRIAL_EXPIRED": {
        return (
          <p className="text-sm font-medium leading-[1.4] mt-5">
            You will need to log in again. Contact your account rep for a longer
            extension.
          </p>
        );
      }
      default: {
        return <LoginLink>Click here to try again</LoginLink>;
      }
    }
  };

  return (
    <Page>
      <MarketingPanel />
      <FormPanel>
        <div className="mt-[15px]">
          <div className="flex items-center">{renderTitle()}</div>
          {renderErrorMessage()}
          <p className="text-base font-medium leading-[1.4] mt-5">
            {renderBelowMessage()}
          </p>
        </div>
      </FormPanel>
    </Page>
  );
};

export default AuthGoogleError;
